import { Box, Skeleton, pn, Typography } from '@compass/core';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { DevExpressReport } from 'components';
import { useRaceData } from '../contexts';
import { RaceFilterControls } from './RaceFilterControls';
import { DashboardCard } from '../components/DashboardCard';
import { RaceDistributionDto } from 'generated/openApi';

const RaceTable = (props: { rows: RaceDistributionDto[]; loading: boolean }) => {
    const appTenantName = useSelector((state: RootState) => state.app.tenantName);
    
    if (props.loading) {
        return <Skeleton variant="rounded" height={400} />;
    }
    
    return (
        <DevExpressReport
            dashboardStyle
            dataSource={props.rows}
            fullPageSize
            showColumnChooser={false}
        >
            <Column
                dataField={pn<RaceDistributionDto>('year')}
                caption="School Year"
                alignment="left"
                cellRender={(data: { value: number }) => `${data.value}-${data.value + 1}`}
            />
            <Column
                dataField={pn<RaceDistributionDto>('raceName')}
                caption="Race"
                alignment="left"
            />
            <Column
                dataField={pn<RaceDistributionDto>('studentCount')}
                caption="Count"
                alignment="left"
            />
            <Column
                dataField={pn<RaceDistributionDto>('percentage')}
                caption="Percentage"
                alignment="left"
                cellRender={(data: { value: number }) => `${data.value}%`}
            />
            <Summary>
                <TotalItem
                    column={pn<RaceDistributionDto>('year')}
                    customizeText={() => 'Total'}
                />
                <TotalItem
                    column={pn<RaceDistributionDto>('studentCount')}
                    summaryType="sum"
                />
                <TotalItem
                    column={pn<RaceDistributionDto>('percentage')}
                    customizeText={() => '100.0%'}
                />
            </Summary>
        </DevExpressReport>
    );
};

const RaceTableCardContent = () => {
    const { data, loading, error } = useRaceData();
    
    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <RaceFilterControls />
                <Typography color="error" align="center" sx={{ mt: 4 }}>
                    {error}
                </Typography>
            </Box>
        );
    }
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <RaceFilterControls />
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <RaceTable rows={data} loading={loading} />
            </Box>
        </Box>
    );
};

export const RaceTableCard = () => {
    const { data, filter } = useRaceData();

    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (!data || data.length === 0) return;

        const yearsLabel = filter.schoolYears.length > 0
            ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
            : 'All Years';
        const exportDate = new Date().toLocaleDateString();
        
        // Metadata headers
        const metadata = [
            '# Race Distribution',
            `# Years: ${yearsLabel}`,
            `# Exported: ${exportDate}`,
            '',
        ];
        
        const headers = 'School Year,Race,Count,Percentage';
        
        const rows = data.map(item => [
            `${item.year}-${item.year + 1}`,
            `"${item.raceName}"`,
            item.studentCount,
            `${item.percentage}%`
        ]);

        const totalCount = data.reduce((sum, item) => sum + item.studentCount, 0);
        rows.push(['Total', '', totalCount, '100.0%']);

        const csvContent = [
            ...metadata,
            headers, 
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const yearsFilename = filter.schoolYears.length > 0
            ? filter.schoolYears.join('_')
            : 'AllYears';
        link.setAttribute('download', `Race_${yearsFilename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <DashboardCard title="Race" onExport={handleExport}>
            <RaceTableCardContent />
        </DashboardCard>
    );
};
